import crews from "csi/access/crews";
import ComparePageStore from "csi/page/ComparePageStore";
import { setLocationRevisionUrl } from "csi/router";
import { resolveNodes } from "csi/service/nodeService";
import { submitWriter } from "csi/service/writerService";
import session from "csi/session";
import sessionConstants from "csi/sessionConstants";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache } from "csi/typings";
import { LOCATION_COMPARE_URL, LOCATION_NOTIFICATION, LOCATION_TAB, LOCATION_URL } from "csi/utility/routerUtility";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, makeObservable, observable, runInAction } from "mobx";
import ApparelBom from "src/Specification/Model/ApparelBom";

class ApparelBOMPageStore extends ComparePageStore {
	private readonly NOTIFICATION_TYPE_SOURCE_BOM = "SourceBOMNotification";

	private readonly PART_MATERIALS_TAB = "PartMaterials";

	@observable private _showEditButton = false;

	constructor(node: C8Node, revisionNode: C8Node, cache: ICache = new WorkingCache()) {
		super(node, revisionNode, cache);
		makeObservable(this);
	}

	/** @override */
	get isCompareBomSelectEnabled(): boolean {
		return !ApparelBom.isSourceBOM(this.pageNode) && !this.readonlyMode;
	}

		/** @override */
		get isCurrentBomSelectEnabled(): boolean {
			return !this.readonlyMode && crews.isInRoles(["Style Viewer"], this.currentNode);
		}

	/** @override change bom selection */
	async _onChangeBOM(item: ComponentItem, isCompareMenu: boolean): Promise<void> {
		const { cache, pageNode } = this;
		const { node } = item;
		const { LinkedHostDataSheet } = node;
		if (ApparelBom.isSourceBOM(pageNode)) {
			await resolveNodes([LinkedHostDataSheet], cache);
			const hostBomlatestRevisionUrl = cache.get(LinkedHostDataSheet).LatestRevision;
			session.setLocationActual(LOCATION_COMPARE_URL, hostBomlatestRevisionUrl, null, this.onChangeCompareUrl, true);
			session.setLocationKey(LOCATION_URL, node.$URL, true);
		} else {
			super._onChangeBOM(item, isCompareMenu);
		}
	};

	/** Get latest Revision */
	get bomLatestRevisionURL(): cnl {
		return this.pageNode.LatestRevision;
	}

	/** Show View Updates Dialog if coming from Notification or there are updates to see for the current revision */
	get showViewUpdatesDialog(): boolean {
		const { isSupplierUser, userSupplierUrl } = sessionConstants;
		const { bomLatestRevisionURL, currentRevisionNode, pageNode } = this;
		const { LinkedBOMUpdateStatus } = currentRevisionNode;
		const currentRevIsLatestRev = bomLatestRevisionURL === currentRevisionNode.$URL;
		const isSupplier = (isSupplierUser || userSupplierUrl);
		const notification = session.getLocationValue(LOCATION_NOTIFICATION);
		let showViewUpdatesDialog = false;

		if (!xml.nodeHasRight(pageNode, xml.RIGHT_WRITE) || !ApparelBom.isSourceBOM(pageNode)) {
			return false;
		}

		if (notification === this.NOTIFICATION_TYPE_SOURCE_BOM) {
			return true;
		}

		if (isSupplier && currentRevIsLatestRev) {
			showViewUpdatesDialog = LinkedBOMUpdateStatus === "LinkedBOMUpdateStatus:UpdatedByHost";
		} else if (!isSupplier && currentRevIsLatestRev) {
			showViewUpdatesDialog = LinkedBOMUpdateStatus === "LinkedBOMUpdateStatus:UpdatedBySource";
		}

		return showViewUpdatesDialog;
	}

	setPartMaterialTab = (showViewUpdatesDialog): void => {
		const { PART_MATERIALS_TAB } = this;
		if (showViewUpdatesDialog && session.getLocationValue(LOCATION_TAB) !== PART_MATERIALS_TAB) {
			// Set the location key to PartMaterials tab
			session.setLocationKey(LOCATION_TAB, PART_MATERIALS_TAB);
		}
	};

	get showEditButton(): boolean {
		const { _showEditButton, readonlyMode } = this;
		return readonlyMode && _showEditButton;
	}

	@action onViewUpdates = async (): Promise<void> => {
		const { bomLatestRevisionURL, cache, currentRevisionNode, pageNode } = this;
		const compareUrl = currentRevisionNode.$URL;
		const currentRevIsLatestRev = bomLatestRevisionURL === currentRevisionNode.$URL;
		// Update the status of linked BOM
		await this.changeLinkedBOMUpdateStatus(compareUrl);

		// Get the list of revision URLs and the current revision's URL
		const revisionUrls = pageNode.Revisions;
		const index = revisionUrls.indexOf(compareUrl);
		const previousRevisionUrl = revisionUrls[index - 1];

		// Resolve nodes for the previous revision URL
		await resolveNodes([previousRevisionUrl], cache);
		// Enable readonly mode and set the comparison revision URL

		runInAction(() => {
			this.readonlyMode = true;
			this._showEditButton = currentRevIsLatestRev;
			this.compareRevisionUrl = compareUrl;
		});
		this.onSelectRevision(previousRevisionUrl, true);
	};

	/** @override */
	onChangeCompareUrl = async (url: cnl): Promise<void> => {
		const { readonlyMode, refreshViewIfExists } = this;
		// used when location compare url changes
		const changed = await this.setCompareRevisionUrl(url);
		if (readonlyMode && xml.isEmptyURL(url)) {
			await setLocationRevisionUrl("");
		} else if (changed) {
			refreshViewIfExists();
		}
	};

	/** @override */
	protected _onChangeReadOnlyMode = (): void => {
		const { readonlyMode, bomLatestRevisionURL } = this;
		this.readonlyMode = !readonlyMode;
		this.onSelectRevision(bomLatestRevisionURL, false);
	};

	/**
	 * Change the Linked BOM Update Status
	 */
	async changeLinkedBOMUpdateStatus(currentRevisionCNL: cnl): Promise<void> {
		const { currentNode } = this;
		if (currentRevisionCNL !== currentNode.CurrentRevision) {
			return;
		}
		const writer = new Writer();
		writer.callMethod("Specification", "UpdateSourceBOMUpdateStatus")
			.addParameter("URL", xml.ATTR_CNL, currentNode.$URL)
			.addParameter("Revision", xml.ATTR_CNL, currentRevisionCNL);
		await submitWriter(writer);
	}
}

export default ApparelBOMPageStore;
